import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Users, UserPlus, CalendarClock, GitBranch, Settings, LogOut, ChevronDown, IndianRupee, PanelLeftClose, PanelLeft } from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/leads', label: 'All Leads', icon: Users },
  { path: '/leads/new', label: 'New Lead', icon: UserPlus },
  { path: '/follow-ups', label: 'Follow Ups', icon: CalendarClock },
  { path: '/funnel', label: 'Funnel', icon: GitBranch },
  { path: '/payments', label: 'Payments', icon: IndianRupee },
  { path: '/masters', label: 'Masters', icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const roleBadge = { admin: 'Admin', admission_head: 'Head', counsellor: 'Counsellor', viewer: 'Viewer' };

  const toggleCollapse = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', String(!prev));
      return !prev;
    });
  };

  return (
    <div className="flex h-screen bg-slate-50" data-testid="main-layout">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? 'w-[72px]' : 'w-64'} bg-white border-r border-slate-200 flex flex-col shrink-0 transition-[width] duration-300 ease-in-out`}
        data-testid="sidebar"
      >
        {/* Logo + collapse toggle */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
          {!collapsed && (
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="https://customer-assets.emergentagent.com/job_admit-track-2/artifacts/ug5k3pex_Screenshot%202026-03-19%20at%2010.10.51%E2%80%AFPM.png"
                alt="MBS College"
                className="h-10 w-10 object-contain rounded shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base font-bold text-slate-900 tracking-tight leading-tight truncate" style={{ fontFamily: 'Manrope' }}>
                  MBS College
                </h1>
                <p className="text-[10px] text-slate-400 font-medium">Admissions CRM</p>
              </div>
            </div>
          )}
          <button
            onClick={toggleCollapse}
            className={`p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors ${collapsed ? 'mx-auto' : ''}`}
            data-testid="sidebar-toggle"
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/leads'}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={18} strokeWidth={1.75} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-slate-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-50 transition-colors ${collapsed ? 'justify-center' : ''}`} data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-slate-900 text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                      <p className="text-xs text-slate-400">{roleBadge[user?.role] || user?.role}</p>
                    </div>
                    <ChevronDown size={14} className="text-slate-400" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
